import React, { useMemo } from "react"

import { eachDayOfInterval, endOfMonth, format, isSameDay, startOfMonth } from "date-fns"
import { AlertTriangle, CheckCircle2, ChevronLeft, ChevronRight, XCircle } from "lucide-react"

import { Button } from "@/components/ui/button"
import { useTrialBlock } from "@/contexts/TrialBlockContext"

const TrialBlockCalendar: React.FC = () => {
  const { filteredBlocks, handleShowDetails, selectedDate, setSelectedDate } = useTrialBlock()
  // Group blocks by date
  const blocksByDate = useMemo(() => {
    const map: Record<string, typeof filteredBlocks> = {}

    filteredBlocks.forEach((block) => {
      const dateKey = block.date
      if (!map[dateKey]) {
        map[dateKey] = []
      }
      map[dateKey].push(block)
    })

    return map
  }, [filteredBlocks])

  // Get all days in current month with proper offset for first day of month
  const calendarDays = useMemo(() => {
    const currentDate = selectedDate || new Date()
    const monthStart = startOfMonth(currentDate)
    const monthEnd = endOfMonth(currentDate)
    const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd })

    // Get the day of the week for the first day (0 = Sunday, 1 = Monday, etc.)
    // Convert to Monday-based index (0 = Monday, 1 = Tuesday, ..., 6 = Sunday)
    const firstDayOfWeek = (monthStart.getDay() + 6) % 7

    // Create empty slots for days before the first day of the month
    const emptyDaysBefore = Array(firstDayOfWeek).fill(null)

    return [...emptyDaysBefore, ...daysInMonth]
  }, [selectedDate])

  const handlePreviousMonth = () => {
    const newDate = new Date(selectedDate || new Date())
    newDate.setMonth(newDate.getMonth() - 1)
    setSelectedDate(newDate)
  }

  const handleNextMonth = () => {
    const newDate = new Date(selectedDate || new Date())
    newDate.setMonth(newDate.getMonth() + 1)
    setSelectedDate(newDate)
  }

  return (
    <div className="space-y-4">
      {/* Calendar Header */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-medium">{format(selectedDate || new Date(), "MMMM yyyy")}</h2>
        <div className="flex items-center space-x-2">
          <Button variant="outline" size="icon" onClick={handlePreviousMonth}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="icon" onClick={() => setSelectedDate(new Date())}>
            Today
          </Button>
          <Button variant="outline" size="icon" onClick={handleNextMonth}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-1">
        {/* Day Headers - Starting with Monday */}
        {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day) => (
          <div
            key={day}
            className="text-center py-2 font-medium text-sm text-palette-jungleGreen500"
          >
            {day}
          </div>
        ))}

        {/* Calendar Days */}
        {calendarDays.map((day, index) => {
          // If day is null, render an empty cell
          if (day === null) {
            return (
              <div
                key={`empty-${index}`}
                className="border rounded-md min-h-[100px] p-1 border-palette-jungleGreen100 bg-palette-jungleGreen000"
              ></div>
            )
          }

          const dateKey = format(day, "yyyy-MM-dd")
          const dayBlocks = blocksByDate[dateKey] || []
          const isToday = isSameDay(day, new Date())

          return (
            <div
              key={dateKey}
              className={`border rounded-md min-h-[100px] p-1 ${
                isToday ? "border-primary bg-primary/5" : "border-palette-jungleGreen200"
              }`}
            >
              <div className="flex justify-between items-start mb-1">
                <span
                  className={`text-sm font-medium p-1 rounded-full w-6 h-6 flex items-center justify-center
                  ${isToday ? "bg-primary text-primary-foreground" : ""}`}
                >
                  {format(day, "d")}
                </span>
              </div>

              <div className="space-y-1 overflow-y-auto max-h-[80px]">
                {dayBlocks.map((block) => (
                  <div
                    key={block.id}
                    className="p-1 text-xs rounded border cursor-pointer hover:bg-palette-jungleGreen100 flex items-center justify-between"
                    onClick={() => handleShowDetails(block.id)}
                  >
                    <span className="truncate">{block.driverName}</span>
                    {block.reviewStatus === "completed" ? (
                      block.result === "Passed" ? (
                        <CheckCircle2 className="h-3.5 w-3.5 text-green-500 ml-1 flex-shrink-0" />
                      ) : (
                        <XCircle className="h-3.5 w-3.5 text-red-500 ml-1 flex-shrink-0" />
                      )
                    ) : (
                      <AlertTriangle className="h-3.5 w-3.5 text-yellow-500 ml-1 flex-shrink-0" />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default TrialBlockCalendar
